<template>
<div>
  <v-card>
    <!-- 頁面標題列（共用元件，融入卡片頂部） -->
    <ListPageHeader
      embedded
      title="經費核銷申請清單（中心）"
      subtitle="檢視與審核各單位經費核銷申請"
      icon="tabler-receipt"
      :count="pageCount"
    >
      <template #actions>
        <v-btn
          color="white"
          variant="flat"
          class="text-primary font-weight-bold"
          :disabled="selectData.length < 1"
          :loading="exportLoading"
          @click="exportData"
        >
          <VIcon icon="tabler-download" class="mr-1" />匯出
        </v-btn>
      </template>
    </ListPageHeader>

    <v-card-item>
      <div class="d-flex align-center mb-1">
        <VIcon icon="tabler-filter" size="20" class="text-primary mr-2" />
        <span class="text-subtitle-1 font-weight-medium">篩選條件</span>
      </div>
      <v-row class="pt-2 pb-5">
        <v-col cols="12" md="2">
          <v-text-field
            label="核銷申請單號"
            v-model="filters.reimburseApplyNo"
            clearable
            prepend-inner-icon="tabler-search"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            :items="associations"
            label="協會"
            v-model="filters.association"
            item-title="deptName"
            item-value="deptNo"
            clearable
            no-data-text="無協會"
            prepend-inner-icon="tabler-search"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="1">
          <v-select
            :items="years"
            label="年度"
            v-model="filters.year"
            item-title="rocYear"
            item-value="adYear"
            clearable
          ></v-select>
        </v-col>
        <v-col cols="12" md="4">
          <v-autocomplete
            :items="applicants"
            label="活動名稱"
            item-title="scheduleTitle"
            item-value="reimburseApplyNo"
            v-model="filters.applicant"
            clearable
            no-data-text="無活動名稱"
            prepend-inner-icon="tabler-search"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="3">
           <AppInput
            v-model="filters.status"
            type="select"
            :remoteItem="deSelectSelect"
            :remoteItemParams="{ tabId}"
            item-title="label"
            item-value="value"
            placeholder="請輸入狀態"
            :disabled="false"
            :loading="false"
            clearable
            :exclude="['Revoked', 'Passed', 'Resubmitted', 'Closed', 'ReturnedForRevision', 'Reviewing']"
            no-default-exclude
          />
        </v-col>
      </v-row>
    </v-card-item>

    <!-- {{selectData}} -->
    <InteractiveDataTable
        v-model:items-per-page="filters.size"
        v-model:page="filters.page"
        :items-length="pageCount"
        v-model="selectData"
        :headers="headers"
        :items="items"
        show-select
        item-value="expReimb.reimburseApplyNo"
        :item-disabled="itemDisabled"
        :item-selectable="itemDisabled"
        :loading="loading"
        empty-text="查無符合條件的核銷申請"
        empty-subtitle="請調整上方篩選條件後再試"
        empty-icon="tabler-receipt-off"
        :items-per-page-options="[
          {value: 10, title: '10'},
          {value: 25, title: '25'},
          {value: 50, title: '50'},
          {value: 100, title: '100'},
        ]"
        :row-props="rowProps"
        :row-clickable="false"
        @row-click="markLastViewed"
      >
        <template v-slot:item.reimburseApplyNo="{ item }">
          <div class="d-inline-flex align-center flex-nowrap">
            <v-btn 
            @click.stop="edit(item)"
            color="primary" 
            variant="text" 
            class="pa-0 idt-link font-weight-bold"
            >
             {{item.expReimb.reimburseApplyNo}}
            </v-btn>
            <v-tooltip location="top" text="複製單號">
              <template #activator="{ props: tipProps }">
                <v-btn
                  v-bind="tipProps"
                  variant="text"
                  icon
                  size="x-small"
                  density="compact"
                  class="ms-1"
                  @click.stop="notifyCopyDocNo(item.expReimb.reimburseApplyNo)"
                >
                  <VIcon icon="tabler-copy" size="18" />
                </v-btn>
              </template>
            </v-tooltip>
          </div>
        </template>
        <template v-slot:item.activityName="{ item }">
          {{!!item.expReimb.activityName?item.expReimb.activityName:item.expReimb.itemName}}
        </template>
        <template v-slot:item.supplementary="{ item }">
           {{item.expReimb.supplementary==="Y"?'是':'否'}}
        </template>
        <template v-slot:item.deptNo="{ item }">
           {{getDeptName(item.expReimb.deptNo)}}<br/>
           {{toCode(item.expReimb.deptNo)}}
        </template>
        <!-- 項目名稱 -->
        <template v-slot:item.applyNo="{ item }">
           {{getApplyNoName(item.expReimb.applyNo)}}<br/>
           {{toCode(item.expReimb.applyNo)}}
        </template>
        <template v-slot:item.eventDate="{ item }">
          <div class="d-inline-flex flex-column text-medium-emphasis text-no-wrap">
            <span class="d-inline-flex align-center flex-nowrap">
              <VIcon icon="tabler-calendar-event" size="16" class="mr-1" />
              {{westernToRoc(item.expReimb.activityStartDate, '-', '-')}}
            </span>
            <span class="d-inline-flex align-center flex-nowrap">
              <VIcon icon="tabler-arrow-down-right" size="16" class="mr-1" />
              {{westernToRoc(item.expReimb.activityEndDate, '-', '-')}}
            </span>
          </div>
        </template>
        <template v-slot:item.status="{ item }">
            <Status 
            :status="item.expReimb.status" 
            :is-red="['Rejected', 'Revoke', 'Revoked']" 
            :is-green="['FPassed', 'SendAfterCorrect']"
            :is-warning="['Draft']"
            :is-info="[]"
            :is-purple="['ReturnedForRevision', 'MReturnedForRevision', 'CorrectAfterApprove']"
            :is-teal="['Reviewing', 'MApproved']"
            :is-indigo="['Resubmitted', 'MPassed']"
            :is-primary="['Submitted', 'FReturnedForRevision']" 
            :is-secondary="['Closed', 'FApproved']" 
            :shared-table-id="tabId" 
            btn-class="x-large" 
            font-class="18px" 
            :status-config="getStatusData" 
          />
        </template>
        
      </InteractiveDataTable>
  </v-card>
</div>
</template>

<script setup lang="ts">
import Status from '../tnm/StatusComponenet.vue'
import { computed, nextTick, onMounted, onUnmounted, reactive } from "vue"
import { useLastViewedRow } from '@/composables/useLastViewedRow'
import { copyPlainText } from '@/utils/deUtil'
definePage({
  meta: {
    action: 'manage', //固定為 manage
    subject: 'EXPENSEREIMBURSEMENTCELIST', //系統代碼(系統授權設定中的系統代碼)
  },
})
const fileUrl = useStorage("drFsUrl", "").value;
const exportLoading = ref(false)
const router = useRouter()
const filters= reactive( {
  applicationName: "",
  reimburseApplyNo: "",
  association: undefined,
  year: undefined,
  applicant: undefined,
  status: undefined,
  page: 1,
  size: 100,
})

const FILTER_STATE_KEY = 'erm_expense_reimbursement_center_list_filter_state'
const restoreListFilters = () => {
  try {
    const raw = sessionStorage.getItem(FILTER_STATE_KEY)
    if (!raw) return
    const saved = JSON.parse(raw) as Record<string, unknown>
    for (const k of Object.keys(filters) as (keyof typeof filters)[]) {
      if (k in saved) (filters as Record<string, unknown>)[k] = saved[k] as never
    }
  } catch { /* ignore */ }
}
const saveListFilters = () => {
  try {
    sessionStorage.setItem(FILTER_STATE_KEY, JSON.stringify({ ...filters }))
  } catch { /* ignore */ }
}
restoreListFilters()

const selectData = ref([])
const { setLastViewed: setLastClickedId, rowProps } = useLastViewedRow(
  'erm_expense_reimbursement_center_list_last_clicked',
  (item: any) => item?.expReimb?.reimburseApplyNo,
)

const markLastViewed = (item: any) => {
  setLastClickedId(item?.expReimb?.reimburseApplyNo)
}
const applicants= reactive( ["申請人1", "申請人2"])
const headers= reactive([
  { title: "核銷申請單號", key: "reimburseApplyNo", icon: "tabler-file-text" },
  { title: "是否為補充核銷", key: "supplementary", icon: "tabler-copy-plus" },
  { title: "單位", key: "deptNo", icon: "tabler-building" },
  { title: "經費預算名稱", key: "expReimb.projectTitle", icon: "tabler-report-money" },
  { title: "活動名稱", key: "activityName", icon: "tabler-clipboard-text" },
  { title: "活動日期", key: "eventDate", icon: "tabler-calendar-event" },
  { title: "狀態", key: "status", icon: "tabler-flag" },
])
const items= reactive<any[]>([])
const page= ref(1)
const pageCount= ref(0)
const loading = ref(true)

const getDeptName = (deptCode: any)=>{
  if(associations.value&&associations.value.length){
    let data = (associations.value as any[]).find((item)=> item.deptNo == deptCode)
    return data?data.deptName:''
  }
}
const getApplyNoName = (applyNoVal: any)=>{
  if(applicants&&applicants.length){
    let data = (applicants as any[]).find((item)=> item.applyNo == applyNoVal)
    return data?data.applyTitle:''
  }
}
// 操作跳轉
const edit = async (item: any) =>{
  setLastClickedId(item.expReimb.reimburseApplyNo)
  await nextTick()
  router.push({ path: '/tca/erm/ExpenseReimbursement', query: { reimburseApplyNo: item.expReimb.id } })
}
const itemDisabled = (item: any) => {
  return (item.expReimb.status !== 'Draft' && item.expReimb.status !== 'Rejected');
}

const notifyCopyDocNo = async (text: string | null | undefined) => {
  const ok = await copyPlainText(text)
  if (ok) {
    $swal({
      icon: 'success',
      title: '已複製到剪貼簿',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 1500,
    })
  } else {
    $swal({
      icon: 'error',
      title: '複製失敗',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2000,
    })
  }
}

// API---------------------------------------------------------------------
// 需求申請名稱input API
const getApplyList = async () => {
  let year = filters.year?filters.year:''
  let association = filters.association?filters.association:''
  try {
    const response = await $api('/tca/expensereimbursementlist/getApplyList', {
      method: 'POST',
      body: {
        year: year,
        deptNo: association,
      },
    });
    // 检查响应的结构
    if (response && response.data && response.data.length > 0) {
      applicants.splice(0, applicants.length, ...response.data);
    } else {
      // 没有数据或错误情况
      applicants.splice(0, applicants.length);  // 清空 applicants
      pageCount.value = 0
      console.error('Fetch error: no data received or response structure unexpected');
    }
  } catch (error) {
    console.error('Error during fetch:', error);
  }
}
// 年分input API
const { data: yearList, execute: fetchFilterYearList } = await useApi<any>(createUrl('/tca/expenserequestprojectlist/getYear', { query: {} }))
// 協會input API
const { data: associationList, execute: fetchFilterAssociationList } = await useApi<any>(createUrl('/tca/expenseReimbursement/getDeptNoList'))
const years = computed(() => yearList ? yearList.value.data : []);
const associations = computed(() => associationList ? associationList.value : []);

const  fetchData =async() =>{
  let deptNoEqual = filters.association?filters.association:null
  let belongYearEqual = filters.year?filters.year:null
  let reimbApplyNo = filters.reimburseApplyNo ? filters.reimburseApplyNo : (filters.applicant ? filters.applicant : null)
  let statusEqual = filters.status?filters.status:null
  let page = filters.page-1
  let size = filters.size
  try {
    loading.value = true
    const response = await $api('/tca/expensereimbursementlist/get', {
      method: 'POST',
      body: {
        deptNoEqual,
        belongYearEqual,
        reimbApplyNo,
        statusEqual,
        page,
        size,
      },
    });

    if (response?.result != null) {
      items.splice(0, items.length, ...(response.result as any[]))
      pageCount.value = response.totalElements ?? 0
      // 目前頁碼超出實際總頁數時（例如篩選後筆數變少），自動回到最後有效頁重抓
      const maxPage = Math.max(1, Math.ceil(pageCount.value / filters.size))
      if (filters.page > maxPage) {
        filters.page = maxPage
        return
      }
    } else {
      items.splice(0, items.length)
      pageCount.value = 0
      console.error('Fetch error:', response?.statusText);
    }
  } catch (error) {
    console.error('Error during fetch:', error);
    items.splice(0, items.length)
    pageCount.value = 0
  } finally {
    loading.value = false
  }
}

const exportData = async(item: any) => {
  exportLoading.value = true;
  const apiUrl='/tca/expensereimbursementlist/exportData'
  await $api(apiUrl, {
    method: 'POST',
    body: Array.from(selectData.value)
  }).then(async(resp) => {
    exportLoading.value = false;
    if (resp.statusCode == 200) {
      const url = await mapFileDownload(fileUrl, resp.data)
      window.open(url, '_blank');
    } else  {
      $swal({
        position: 'top-end',
        icon: 'error',
        title: '匯出失敗,錯誤訊息',
        text: resp.data,
        showConfirmButton: false,
        // timer: 1500,
        customClass: {
          confirmButton: 'btn btn-primary',
        },
        buttonsStyling: false,
      });
    }
  })
};

const tabId = ref('ExpenseReimbursement')
const { data: getStatusData, execute: fetchstatusDatas } = await useApi<any>(createUrl('/q/de/select/select/select/deSelect', {
  query: {
    tabId: tabId.value
  },
}))

onMounted(()=>{
  getApplyList()
  fetchData()
})
watch(() => filters.association,()=>{
  getApplyList()
})
watch(() => filters.year,()=>{
  getApplyList()
})
watch(filters, ()=>{
  fetchData()
})

onUnmounted(() => saveListFilters())

</script>
